<div class="text-fg w-full max-w-none p-6">
  @if (loading()) {
    <div class="flex items-center justify-center gap-2 p-12 text-sm text-muted">
      <ui-spinner size="sm" />
      <span>Loading message...</span>
    </div>
  } @else if (error() && !item()) {
    <div class="p-8 text-center text-danger">
      <p>{{ error() }}</p>
      <button type="button" uiButton variant="outline" class="mt-3" (click)="load()">Retry</button>
    </div>
  } @else if (item(); as mq) {
    <header class="mb-6">
      <button type="button" class="mb-3 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-sm text-muted hover:text-primary" (click)="goBack()">
        <ng-icon name="lucideArrowLeft" size="16" />
        Back
      </button>
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="m-0 flex items-center gap-2 font-heading text-2xl font-bold text-fg">
            <ng-icon name="lucideMessageSquare" size="22" />
            Message
          </h1>
          <div class="mt-2 flex flex-wrap items-center gap-2">
            <ui-badge [variant]="statusVariant(mq.status.value)">{{ mq.status.title }}</ui-badge>
            <ui-badge [variant]="deliveryVariant(mq.delivery_status.value)" title="Delivery reported by the provider">
              {{ mq.delivery_status.title }}
            </ui-badge>
            <ui-badge variant="info">{{ mq.channel.title }}</ui-badge>
            <ui-badge>{{ mq.application.title }}</ui-badge>
            @if (mq.processor) {
              <ui-badge class="font-mono">{{ mq.processor }}</ui-badge>
            }
          </div>
        </div>
        <div class="flex gap-2">
          @if (canResend()) {
            <button type="button" uiButton [disabled]="resending()" (click)="resend()" title="Resend this message">
              @if (resending()) {
                <ui-spinner size="sm" />
              } @else {
                <ng-icon name="lucideSend" size="14" />
              }
              <span>Resend</span>
            </button>
          }
          <button type="button" uiButton variant="outline" aria-label="Refresh" (click)="load()" title="Refresh">
            <ng-icon name="lucideRefreshCw" size="16" />
          </button>
        </div>
      </div>
    </header>

    @if (error()) {
      <ui-alert variant="danger">
        <div class="flex items-center justify-between gap-2">
          <span>{{ error() }}</span>
          <button type="button" class="cursor-pointer border-0 bg-transparent p-0.5 text-current hover:opacity-70" (click)="error.set(null)">
            <ng-icon name="lucideX" size="14" />
          </button>
        </div>
      </ui-alert>
    }

    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <h3 class="mb-3 mt-0 text-sm font-semibold text-fg-secondary">Delivery</h3>
      <div class="grid grid-cols-1 gap-x-6 gap-y-2.5 md:grid-cols-2">
        <div class="flex flex-col gap-0.5 md:col-span-2">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Message ID</span>
          <span class="inline-flex cursor-pointer items-center gap-1 break-all text-sm hover:text-primary" (click)="copy(mq.id)" title="Copy">
            <code class="rounded bg-surface-muted px-1 py-0.5 font-mono text-xs">{{ mq.id }}</code>
            <ng-icon name="lucideClipboard" size="12" />
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Recipient</span>
          <span class="text-sm">{{ mq.recipient_masked }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Language</span>
          <span class="text-sm">{{ mq.language }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Provider</span>
          <span class="text-sm">
            {{ mq.processor_provider?.primary ?? '—' }}
            @if (mq.processor_provider?.secondary) {
              <span class="text-muted">({{ mq.processor_provider?.secondary }})</span>
            }
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Merchant application</span>
          <span class="text-sm">
            {{ mq.merchant_application?.primary ?? '—' }}
            @if (mq.merchant_application?.secondary) {
              <span class="text-muted">({{ mq.merchant_application?.secondary }})</span>
            }
          </span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Attempts</span>
          <span class="text-sm">{{ mq.attempt_count }} / {{ mq.max_retries }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Created</span>
          <span class="text-sm">{{ formatDate(mq.created_at) }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Sent at</span>
          <span class="text-sm">{{ formatDate(mq.sent_at) }}</span>
        </div>
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Delivered at</span>
          <span class="text-sm">{{ formatDate(mq.delivered_at) }}</span>
        </div>
        @if (mq.campaign) {
          <div class="flex flex-col gap-0.5">
            <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Campaign</span>
            <span class="text-sm">{{ mq.campaign.primary }} <span class="text-muted">{{ mq.campaign.secondary }}</span></span>
          </div>
        }
        <div class="flex flex-col gap-0.5">
          <span class="text-[0.72rem] font-medium uppercase tracking-wide text-muted">Updated</span>
          <span class="text-sm">{{ formatDate(mq.updated_at) }}</span>
        </div>
      </div>
    </section>

    @if (mq.last_error) {
      <section class="mb-4 rounded-lg border border-danger bg-card px-5 py-4">
        <h3 class="mb-2 mt-0 text-sm font-semibold text-danger">Last error</h3>
        <pre class="m-0 whitespace-pre-wrap break-all font-mono text-xs">{{ mq.last_error }}</pre>
      </section>
    }

    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <div class="mb-2 flex items-center justify-between gap-2">
        <h3 class="m-0 text-sm font-semibold text-fg-secondary">
          Content
          @if (mq.subject) {
            <span class="ml-2 font-normal text-muted">— {{ mq.subject }}</span>
          }
        </h3>
        @if (canPreview()) {
          <button type="button" uiButton variant="outline" size="sm" (click)="previewOpen.set(true)">
            <ng-icon name="lucideEye" size="14" aria-hidden="true" />
            Preview
          </button>
        }
      </div>
      <p class="mb-2 mt-0 text-xs text-muted">One-time codes are masked.</p>
      <pre class="m-0 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded bg-surface-muted p-3 font-mono text-xs">{{ mq.message }}</pre>
    </section>

    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <h3 class="mb-3 mt-0 text-sm font-semibold text-fg-secondary">Attempts ({{ mq.responses.length }})</h3>
      @if (isEmpty(mq.responses)) {
        <p class="m-0 text-sm text-muted">No attempt recorded yet.</p>
      } @else {
        <div class="flex flex-col gap-2">
          @for (attempt of mq.responses; track $index) {
            <div class="rounded border border-border">
              <button type="button" class="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent px-3 py-2 text-left text-sm hover:text-primary" (click)="toggleAttempt($index)">
                <span>Attempt {{ $index + 1 }}</span>
                <span class="text-xs text-muted">{{ openAttempts().has($index) ? 'Hide' : 'Show' }}</span>
              </button>
              @if (openAttempts().has($index)) {
                <pre class="m-0 max-h-96 overflow-auto border-t border-border p-3 font-mono text-xs">{{ formatJson(attempt) }}</pre>
              }
            </div>
          }
        </div>
      }
    </section>

    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <button type="button" class="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent p-0 text-sm font-semibold text-fg-secondary" (click)="showRequest.set(!showRequest())">
        <span>Request payload</span>
        <span class="text-xs font-normal text-muted">{{ showRequest() ? 'Hide' : 'Show' }}</span>
      </button>
      @if (showRequest()) {
        <pre class="mb-0 mt-3 max-h-96 overflow-auto rounded bg-surface-muted p-3 font-mono text-xs">{{ isEmpty(mq.request_payload) ? '—' : formatJson(mq.request_payload) }}</pre>
      }
    </section>

    <section class="mb-4 rounded-lg border border-border bg-card px-5 py-4">
      <button type="button" class="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent p-0 text-sm font-semibold text-fg-secondary" (click)="showConfirmation.set(!showConfirmation())">
        <span>Delivery confirmation</span>
        <span class="text-xs font-normal text-muted">{{ showConfirmation() ? 'Hide' : 'Show' }}</span>
      </button>
      @if (showConfirmation()) {
        <pre class="mb-0 mt-3 max-h-96 overflow-auto rounded bg-surface-muted p-3 font-mono text-xs">{{ isEmpty(mq.delivery_confirmation) ? '—' : formatJson(mq.delivery_confirmation) }}</pre>
      }
    </section>

    @if (copyToast()) {
      <div class="fixed bottom-6 right-6 rounded bg-fg px-3 py-2 text-xs text-bg">Copied</div>
    }
  }
</div>

@if (canPreview()) {
  <ui-modal [open]="previewOpen()" title="Email preview" size="xl" (closed)="previewOpen.set(false)">
    <div class="mb-3 flex items-center justify-between gap-2">
      <p class="m-0 truncate text-sm"><strong>{{ item()?.subject }}</strong></p>
      <div class="flex gap-1">
        <button type="button" uiButton size="sm" [variant]="previewMobile() ? 'outline' : 'primary'" (click)="previewMobile.set(false)">Desktop</button>
        <button type="button" uiButton size="sm" [variant]="previewMobile() ? 'primary' : 'outline'" (click)="previewMobile.set(true)">Mobile</button>
      </div>
    </div>
    <div class="flex justify-center rounded-md bg-surface-muted p-3">
      <iframe
        title="Email preview"
        sandbox=""
        class="h-[65dvh] rounded-md border border-border bg-white transition-all"
        [class.w-full]="!previewMobile()"
        [class.w-[375px]]="previewMobile()"
        [srcdoc]="previewHtml()"
      ></iframe>
    </div>
    <ng-container modal-footer>
      <button type="button" uiButton variant="outline" (click)="previewOpen.set(false)">Close</button>
    </ng-container>
  </ui-modal>
}
